/**
 * Minimal, dependency-free MD5 implementation used for AdTraction's hashed
 * email (xd) field.
 *
 * crypto-js is a pure UMD package with no ESM support, which breaks Nuxt/Vite
 * SSR bundling (it evaluates its "global" branch with an undefined `this`,
 * crashing at import time). This self-contained implementation avoids that
 * CJS/ESM interop issue entirely.
 *
 * Based on the public domain JS MD5 implementation by Paul Johnston et al.
 * (http://pajhome.org.uk/crypt/md5), adapted for UTF-8 input.
 */

function safeAdd(x: number, y: number): number {
  const lsw = (x & 0xffff) + (y & 0xffff);
  const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
  return (msw << 16) | (lsw & 0xffff);
}

function bitRotateLeft(num: number, cnt: number): number {
  return (num << cnt) | (num >>> (32 - cnt));
}

function md5cmn(q: number, a: number, b: number, x: number, s: number, t: number): number {
  return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
}

function md5ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn((b & c) | (~b & d), a, b, x, s, t);
}

function md5gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn((b & d) | (c & ~d), a, b, x, s, t);
}

function md5hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn(b ^ c ^ d, a, b, x, s, t);
}

function md5ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return md5cmn(c ^ (b | ~d), a, b, x, s, t);
}

function binlMd5(input: number[], length: number): number[] {
  input[length >> 5] |= 0x80 << (length % 32);
  input[(((length + 64) >>> 9) << 4) + 14] = length;

  let a = 1732584193;
  let b = -271733879;
  let c = -1732584194;
  let d = 271733878;

  for (let i = 0; i < input.length; i += 16) {
    const olda = a;
    const oldb = b;
    const oldc = c;
    const oldd = d;

    a = md5ff(a, b, c, d, input[i], 7, -680876936);
    d = md5ff(d, a, b, c, input[i + 1], 12, -389564586);
    c = md5ff(c, d, a, b, input[i + 2], 17, 606105819);
    b = md5ff(b, c, d, a, input[i + 3], 22, -1044525330);
    a = md5ff(a, b, c, d, input[i + 4], 7, -176418897);
    d = md5ff(d, a, b, c, input[i + 5], 12, 1200080426);
    c = md5ff(c, d, a, b, input[i + 6], 17, -1473231341);
    b = md5ff(b, c, d, a, input[i + 7], 22, -45705983);
    a = md5ff(a, b, c, d, input[i + 8], 7, 1770035416);
    d = md5ff(d, a, b, c, input[i + 9], 12, -1958414417);
    c = md5ff(c, d, a, b, input[i + 10], 17, -42063);
    b = md5ff(b, c, d, a, input[i + 11], 22, -1990404162);
    a = md5ff(a, b, c, d, input[i + 12], 7, 1804603682);
    d = md5ff(d, a, b, c, input[i + 13], 12, -40341101);
    c = md5ff(c, d, a, b, input[i + 14], 17, -1502002290);
    b = md5ff(b, c, d, a, input[i + 15], 22, 1236535329);

    a = md5gg(a, b, c, d, input[i + 1], 5, -165796510);
    d = md5gg(d, a, b, c, input[i + 6], 9, -1069501632);
    c = md5gg(c, d, a, b, input[i + 11], 14, 643717713);
    b = md5gg(b, c, d, a, input[i], 20, -373897302);
    a = md5gg(a, b, c, d, input[i + 5], 5, -701558691);
    d = md5gg(d, a, b, c, input[i + 10], 9, 38016083);
    c = md5gg(c, d, a, b, input[i + 15], 14, -660478335);
    b = md5gg(b, c, d, a, input[i + 4], 20, -405537848);
    a = md5gg(a, b, c, d, input[i + 9], 5, 568446438);
    d = md5gg(d, a, b, c, input[i + 14], 9, -1019803690);
    c = md5gg(c, d, a, b, input[i + 3], 14, -187363961);
    b = md5gg(b, c, d, a, input[i + 8], 20, 1163531501);
    a = md5gg(a, b, c, d, input[i + 13], 5, -1444681467);
    d = md5gg(d, a, b, c, input[i + 2], 9, -51403784);
    c = md5gg(c, d, a, b, input[i + 7], 14, 1735328473);
    b = md5gg(b, c, d, a, input[i + 12], 20, -1926607734);

    a = md5hh(a, b, c, d, input[i + 5], 4, -378558);
    d = md5hh(d, a, b, c, input[i + 8], 11, -2022574463);
    c = md5hh(c, d, a, b, input[i + 11], 16, 1839030562);
    b = md5hh(b, c, d, a, input[i + 14], 23, -35309556);
    a = md5hh(a, b, c, d, input[i + 1], 4, -1530992060);
    d = md5hh(d, a, b, c, input[i + 4], 11, 1272893353);
    c = md5hh(c, d, a, b, input[i + 7], 16, -155497632);
    b = md5hh(b, c, d, a, input[i + 10], 23, -1094730640);
    a = md5hh(a, b, c, d, input[i + 13], 4, 681279174);
    d = md5hh(d, a, b, c, input[i], 11, -358537222);
    c = md5hh(c, d, a, b, input[i + 3], 16, -722521979);
    b = md5hh(b, c, d, a, input[i + 6], 23, 76029189);
    a = md5hh(a, b, c, d, input[i + 9], 4, -640364487);
    d = md5hh(d, a, b, c, input[i + 12], 11, -421815835);
    c = md5hh(c, d, a, b, input[i + 15], 16, 530742520);
    b = md5hh(b, c, d, a, input[i + 2], 23, -995338651);

    a = md5ii(a, b, c, d, input[i], 6, -198630844);
    d = md5ii(d, a, b, c, input[i + 7], 10, 1126891415);
    c = md5ii(c, d, a, b, input[i + 14], 15, -1416354905);
    b = md5ii(b, c, d, a, input[i + 5], 21, -57434055);
    a = md5ii(a, b, c, d, input[i + 12], 6, 1700485571);
    d = md5ii(d, a, b, c, input[i + 3], 10, -1894986606);
    c = md5ii(c, d, a, b, input[i + 10], 15, -1051523);
    b = md5ii(b, c, d, a, input[i + 1], 21, -2054922799);
    a = md5ii(a, b, c, d, input[i + 8], 6, 1873313359);
    d = md5ii(d, a, b, c, input[i + 15], 10, -30611744);
    c = md5ii(c, d, a, b, input[i + 6], 15, -1560198380);
    b = md5ii(b, c, d, a, input[i + 13], 21, 1309151649);
    a = md5ii(a, b, c, d, input[i + 4], 6, -145523070);
    d = md5ii(d, a, b, c, input[i + 11], 10, -1120210379);
    c = md5ii(c, d, a, b, input[i + 2], 15, 718787259);
    b = md5ii(b, c, d, a, input[i + 9], 21, -343485551);

    a = safeAdd(a, olda);
    b = safeAdd(b, oldb);
    c = safeAdd(c, oldc);
    d = safeAdd(d, oldd);
  }

  return [a, b, c, d];
}

function binlToRstr(input: number[]): string {
  let output = '';
  const length32 = input.length * 32;

  for (let i = 0; i < length32; i += 8) {
    output += String.fromCharCode((input[i >> 5] >>> (i % 32)) & 0xff);
  }

  return output;
}

function rstrToBinl(input: string): number[] {
  const output: number[] = [];
  const outputLength = ((input.length + 3) >> 2) || 1;

  for (let i = 0; i < outputLength; i += 1) {
    output[i] = 0;
  }

  const length8 = input.length * 8;
  for (let i = 0; i < length8; i += 8) {
    output[i >> 5] |= (input.charCodeAt(i / 8) & 0xff) << (i % 32);
  }

  return output;
}

function rstrToHex(input: string): string {
  const hexTab = '0123456789abcdef';
  let output = '';

  for (let i = 0; i < input.length; i += 1) {
    const x = input.charCodeAt(i);
    output += hexTab.charAt((x >>> 4) & 0x0f) + hexTab.charAt(x & 0x0f);
  }

  return output;
}

function utf8Encode(str: string): string {
  return unescape(encodeURIComponent(str));
}

/**
 * Returns the MD5 hash of the given string as a lowercase hex string.
 */
export function md5(input: string): string {
  const utf8Input = utf8Encode(input);
  return rstrToHex(binlToRstr(binlMd5(rstrToBinl(utf8Input), utf8Input.length * 8)));
}
